<script>
import { Interval } from 'luxon'

export default {
    name: 'PlanningLine',
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        line: {
            type: Object,
            default() {
                return {
                    from: null,
                    to: null,
                    selected: false,
                }
            },
        },
    },
    emits: ['lineUpdated', 'duplicateToNextDay', 'dropLine'],
    data() {
        return {
            readonly: true,
        }
    },
    computed: {
        weekBoundedDuration() {
            const nextWeekStart = this.line.from.startOf('week').plus({ weeks: 1 })
            const to = this.line.to < nextWeekStart ? this.line.to : nextWeekStart

            return to.diff(this.line.from, ['hours', 'minutes'])
        },
        error() {
            if (!this.line.from && !this.line.from.isValid) {
                return 'invalid-from'
            }

            if (!this.line.to && !this.line.to.isValid) {
                return 'invalid-to'
            }

            const interval = Interval.fromDateTimes(this.line.from, this.line.to)

            if (!interval.isValid || interval.length('seconds') == 0) {
                return 'invalid-interval'
            }

            const overlapping = this.$parent.timetable.filter(function (line) {
                const lineInterval = Interval.fromDateTimes(line.from, line.to)

                return interval.overlaps(lineInterval)
            })

            if (overlapping.length > 1) {
                return 'overlapping'
            }

            return null
        },
    },
    methods: {
        setFromDate($event) {
            const date = $event.target.value.split('-')

            // eslint-disable-next-line vue/no-mutating-props
            this.line.from = this.line.from.set({
                year: date[0],
                month: date[1],
                day: date[2],
            })
        },
        setFromTime($event) {
            const date = $event.target.value.split(':')

            // eslint-disable-next-line vue/no-mutating-props
            this.line.from = this.line.from.set({
                hours: date[0],
                minutes: date[1],
            })
        },
        setToDate($event) {
            const date = $event.target.value.split('-')

            // eslint-disable-next-line vue/no-mutating-props
            this.line.to = this.line.to.set({
                year: date[0],
                month: date[1],
                day: date[2],
            })
        },
        setToTime($event) {
            const date = $event.target.value.split(':')

            // eslint-disable-next-line vue/no-mutating-props
            this.line.to = this.line.to.set({
                hours: date[0],
                minutes: date[1],
            })
        },
    },
}
</script>

<template>
    <tr :class="{ 'bg-fuel-yellow--lighter': error }">
        <td>
            <div v-if="readonly">
                Du <span v-text="line.from.toFormat('DDDD')"></span> -
                <strong v-text="line.from.toFormat('t')"></strong>
            </div>
            <div v-else class="input-group">
                <div class="input-group-prepend">
                    <div class="input-group-text">Du</div>
                </div>
                <input
                    type="date"
                    :disabled="disabled"
                    :value="line.from && line.from.toISODate()"
                    class="form-control"
                    :class="{ 'is-invalid': error }"
                    @blur="$emit('lineUpdated', JSON.stringify(line))"
                    @input="setFromDate($event)"
                />
                <input
                    type="time"
                    :disabled="disabled"
                    :value="
                        line.from &&
                        line.from.toISOTime({
                            includeOffset: false,
                            suppressMilliseconds: true,
                            extendedZone: false,
                            suppressSeconds: true,
                        })
                    "
                    class="form-control"
                    :class="{ 'is-invalid': error }"
                    @blur="$emit('lineUpdated', JSON.stringify(line))"
                    @input="setFromTime($event)"
                />
                <div v-if="error == 'invalid-from'" class="invalid-feedback">Date invalide</div>
                <div v-else-if="error == 'invalid-interval'" class="invalid-feedback">Plage horaire invalide</div>
                <div v-else-if="error == 'overlapping'" class="invalid-feedback">
                    Conflit entre plusieurs plages horaires
                </div>
            </div>
        </td>
        <td>
            <div v-if="readonly">
                Au <span v-text="line.to.toFormat('DDDD')"></span> - <strong v-text="line.to.toFormat('t')"></strong>
            </div>
            <div v-else class="input-group">
                <div class="input-group-prepend">
                    <div class="input-group-text">Au</div>
                </div>
                <input
                    type="date"
                    :disabled="disabled"
                    :value="line.to.toISODate()"
                    class="form-control"
                    :min="line.from.date"
                    :class="{ 'is-invalid': error }"
                    @blur="$emit('lineUpdated', JSON.stringify(line))"
                    @input="setToDate($event)"
                />
                <input
                    type="time"
                    :disabled="disabled"
                    :value="
                        line.to.toISOTime({
                            includeOffset: false,
                            suppressMilliseconds: true,
                            extendedZone: false,
                            suppressSeconds: true,
                        })
                    "
                    class="form-control"
                    :class="{ 'is-invalid': error }"
                    @blur="$emit('lineUpdated', JSON.stringify(line))"
                    @input="setToTime($event)"
                />
                <div v-if="error == 'invalid-to'" class="invalid-feedback">Date invalide</div>
            </div>
        </td>
        <td>{{ weekBoundedDuration.hours }}h{{ String(weekBoundedDuration.minutes).padStart(2, '0') }}m</td>
        <td v-show="!disabled" class="text-right">
            <button
                type="button"
                class="btn"
                :class="{ active: !readonly }"
                title="Modifier"
                @click="readonly = !readonly"
            >
                <i class="fa-solid color-fuel-yellow" :class="{ 'fa-pencil': readonly, 'fa-save': !readonly }"></i>
            </button>
            <button type="button" class="btn" title="Dupliquer le jour suivant" @click="$emit('duplicateToNextDay')">
                <i class="fa-solid fa-arrow-turn-down color-fuel-yellow"></i>
            </button>
            <button type="button" title="Supprimer" class="btn" @click="$emit('dropLine')">
                <i class="fa-solid fa-trash color-dark-raspberry h5"></i>
            </button>
        </td>
    </tr>
</template>
